export interface User {
  _id?: string;
  id: string;
  email: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  username?: string;
  phone?: string; // Changed from phoneNumber to phone to match backend
  phoneNumber?: string; // Keep for backward compatibility
  images?: string;
  profileImage?: string;
  roles: string[]; // Changed from role to roles array
  isActive: boolean;
  verified?: string;
  provider?: string;
  providerId?: string;    // Optional link to ServiceProvider document (backend: providerId)
  created?: string;
  modified?: string;
  description?: string;
  datecreated: Date;
  datemodified: Date;
  lastLogin?: string;
  joinedDate?: string;
  lastLoginAt?: Date;
  bookingsCount?: number;
  rating?: number;
  status?: 'active' | 'inactive' | 'suspended';
  totalBookings?: number;
  preferences?: UserPreferences;
  location?: UserLocation;
  token?: string; // JWT token for authentication
  isGuest?: boolean; // Flag to identify guest users
  twoFactorEnabled?: boolean;
}

export interface UserUpdate {
  firstName?: string;
  lastName?: string;
  phone?: string;
  avatar?: string;
}

export interface UserPreferences {
  theme: 'light' | 'dark' | 'auto';
  language: string;
  notifications: NotificationPreferences;
  privacy: PrivacySettings;
}

export interface NotificationPreferences {
  push: boolean;
  email: boolean;
  sms: boolean;
  bookingUpdates: boolean;
  promotions: boolean;
}

export interface PrivacySettings {
  showProfile: boolean;
  showLocation: boolean;
  allowTracking: boolean;
}

export interface UserLocation {
  latitude?: number;
  longitude?: number;
  address?: string;
  city?: string;
  province?: string;
  country?: string;
}

export enum UserRole {
  USER = 'user',
  PROVIDER = 'provider',
  CUSTOMER = 'customer',
  SERVICE_PROVIDER = 'service_provider',
  SUPERADMIN = 'superadmin',
  ADMIN = 'admin',
  AI = 'ai',
  MODERATOR = 'moderator',
  Driver = 'driver'
}

const ADMIN_ROLE_ALIASES = ['admin', 'administrator', 'superadmin', 'super_admin'];
const SERVICE_PROVIDER_ROLE_ALIASES = ['service_provider', 'serviceprovider', 'service-provider', 'service provider', 'provider'];

/**
 * Get the highest priority role from a user's roles array
 * Priority order: superadmin/admin > service_provider/provider > moderator > driver > user/customer
 */
export function getHighestPriorityRole(roles: string[]): string {
  if (!roles || roles.length === 0) return 'user';
  
  // Convert to lowercase for comparison
  const normalizedRoles = roles.map(role => role.toLowerCase());
  
  // Define role priorities (higher number = higher priority)
  const rolePriority: { [key: string]: number } = {
    'admin': 100,
    'superadmin': 100,
    'super_admin': 100,
    'ai': 90,
    'service_provider': 80,
    'serviceprovider': 80,
    'service-provider': 80,
    'service provider': 80,
    'provider': 80,
    'moderator': 60,
    'driver': 40,
    'user': 20,
    'customer': 20
  };
  
  // Find the role with highest priority
  let highestRole = 'user';
  let highestPriority = 0;
  
  for (const role of normalizedRoles) {
    const priority = rolePriority[role] || 20; // Default to user priority
    if (priority > highestPriority) {
      highestPriority = priority;
      highestRole = role;
    }
  }
  
  return highestRole;
}

/**
 * Check if user has a specific role (case-insensitive)
 */
export function hasRole(userRoles: string[], targetRole: string): boolean {
  if (!userRoles || userRoles.length === 0) return false;
  
  const normalizedTargetRole = targetRole.toLowerCase();
  const normalizedUserRoles = userRoles.map(role => role.toLowerCase());
  
  // Handle role aliases
  if (ADMIN_ROLE_ALIASES.includes(normalizedTargetRole)) {
    return normalizedUserRoles.some(role => ADMIN_ROLE_ALIASES.includes(role));
  }
  
  if (SERVICE_PROVIDER_ROLE_ALIASES.includes(normalizedTargetRole)) {
    return normalizedUserRoles.some(role => SERVICE_PROVIDER_ROLE_ALIASES.includes(role));
  }
  
  return normalizedUserRoles.includes(normalizedTargetRole);
}

/**
 * Check if user has admin privileges
 */
export function isAdmin(userRoles: string[]): boolean {
  return hasRole(userRoles, 'admin') || hasRole(userRoles, 'administrator');
}

/**
 * Check if user is a service provider
 */
export function isServiceProvider(userRoles: string[]): boolean {
  return hasRole(userRoles, 'service_provider');
}

export interface UserProfile {
  uid: string;
  email: string;
  displayName: string;
  photoURL?: string;
  roles?: string[]; // Changed from role to roles array
  isOnline?: boolean;
  lastSeen?: any; // Firebase Timestamp
  location?: UserLocation;
  rating?: number;
  completedJobs?: number;
  availability?: boolean; // For service providers - online/offline status
  serviceCategories?: string[]; // Services they can provide
}

export interface UserListParams {
  pageSize?: number;
  skip?: number;
  sort?: string;
  search?: string;
  name?: string;
  role?: string;
  isActive?: boolean;
  isDeleted?: boolean;
  startDate?: string;
  endDate?: string;
}

export interface UserStats {
  total: number;
  customers: number;
  providers: number;
  admins: number;
  active: number;
  inactive: number;
  suspended: number;
}
